import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollReveal from '../../components/ui/ScrollReveal';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const testimonials = [
  {
    quote:
      'I have been training with Laura for 4 years and it has been an amazing journey. We started training towards the end of 2019, but it was not long before Covid hit. However, we continued to train via video in the following months until we could get back to the gym. So even now we can revert to a video session if I am unable to get in. Today we train at GymBox. Laura normally creates a new programme every couple of months, and we have focused on all parts of the body. I can safely say that my core strength is much better than it has ever been in my life. My overall fitness and body condition is now very good. Laura is very patient but also has the necessary Scottish hard streak to ensure that you do not give up on your exercises and that you continue to improve your personal bests. Give it a go!',
    name: 'Phil',
    duration: 'Client since 2019',
    result: 'Core strength and overall fitness transformed',
    service: '1-2-1 PT',
    image: '/images/IMG_5431_edited_edited.jpg',
  },
  {
    quote:
      "I have had the pleasure of training with Laura Martin since February 2023 and there are not enough adjectives to describe how fantastic she is! Her energy is unmatched and she can even make single leg squats enjoyable. She knows her stuff and is unbelievably talented, and she can point you in the right direction with the things she does not know. That is if she has not gone away to become a master in it! It has been an absolute joy and honour to train with her and I am so grateful to have had the pleasure to meet her! If you need a trainer who will light up even your darkest days, then she is your girl!",
    name: 'Millie',
    duration: 'Client since Feb 2023',
    result: 'Energy, confidence, and consistency improved',
    service: '1-2-1 PT',
    image: '/images/IMG_1831_edited.jpg',
  },
];

export default function Testimonials() {
  useSEO({
    title: 'Client Results & Testimonials | Laura Martin Fitness',
    description:
      "Real client transformations with Laura Martin Fitness. See how Laura Martin Fitness has transformed 200+ clients.",
  });

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Header />
      <main>
        <section className="relative h-[50vh] min-h-[400px] lg:h-[70vh] lg:min-h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img src="/images/MIH02370_2.jpg" alt="Laura Martin client results personal training London" className="w-full h-full object-cover object-[50%_15%]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917]/80 via-[#1c1917]/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #faf8f5 0%, rgba(250,248,245,0.35) 18%, transparent 42%)' }} />
          </div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36">
            <ScrollReveal className="absolute bottom-0 pb-10 lg:pb-24 max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <span className="w-8 h-px bg-[#c9a96e]" />
                <span className="text-[#c9a96e] text-[10px] font-sans font-semibold tracking-[0.32em] uppercase">Client Stories</span>
              </div>
              <h1 className="text-white mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.8rem, 6vw, 5rem)', fontWeight: 600, lineHeight: 1.0 }}>
                Real People.
                <br />
                <em className="text-[#c9a96e] not-italic">Real Results.</em>
              </h1>
            </ScrollReveal>
          </div>
        </section>

        <section className="bg-[#faf8f5] py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <ScrollReveal key={t.name} delay={i * 70}>
                  <div className="bg-white rounded-2xl overflow-hidden flex flex-col h-full shadow-sm border border-stone-200 hover:shadow-md hover:border-[#c9a96e]/20 transition-all duration-300">
                    <div className="h-72 overflow-hidden">
                      <img
                        src={t.image}
                        alt={`${t.name} client transformation with Laura Martin Fitness`}
                        loading="lazy"
                        className="w-full h-full object-cover object-[50%_15%]"
                      />
                    </div>
                    <div className="p-8 flex flex-col flex-1">
                    <div className="flex gap-1 mb-6">{[...Array(5)].map((_, j) => <i key={j} className="ri-star-fill text-[#c9a96e] text-xs" />)}</div>
                    <blockquote className="text-stone-600 font-sans text-sm leading-relaxed flex-1 mb-8 italic">"{t.quote}"</blockquote>
                    <div className="border-t border-stone-100 pt-6">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <p className="text-stone-900 font-sans font-semibold text-sm">{t.name}</p>
                          <p className="text-stone-400 font-sans text-xs">{t.duration}</p>
                        </div>
                        <span className="px-2.5 py-1 bg-[#c9a96e]/10 text-[#c9a96e] text-[9px] font-sans font-semibold tracking-widest uppercase rounded-full whitespace-nowrap">{t.service}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#c9a96e]" />
                        <p className="text-[#c9a96e] font-sans text-xs font-medium">{t.result}</p>
                      </div>
                    </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#faf8f5] py-16 lg:py-24 text-center border-t border-stone-200">
          <ScrollReveal>
            <div className="max-w-xl mx-auto px-6">
              <h2 className="text-stone-900 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.4rem', fontWeight: 600 }}>Your Story Starts Here.</h2>
              <p className="text-stone-500 font-sans text-sm leading-relaxed mb-8">Join hundreds of clients who've already transformed their bodies and their lives with Laura.</p>
              <Link to="/contact" className="inline-block px-9 py-4 bg-[#c9a96e] text-white text-[10px] font-sans font-bold tracking-[0.28em] uppercase rounded-full hover:bg-[#a8863f] hover:shadow-xl hover:shadow-[#c9a96e]/20 hover:-translate-y-0.5 transition-all duration-300">
                Book Free Consultation
              </Link>
            </div>
          </ScrollReveal>
        </section>
      </main>
      <Footer />
    </div>
  );
}
